/*
 * Fraunhofer Institute for Computer Graphics Research (IGD)
 * Competence Center for Information Visualization and Visual Analytics
 *
 * Copyright (c) 2018 Fraunhofer IGD. All rights reserved.
 *
 * This source code is property of the Fraunhofer IGD and underlies
 * copyright restrictions. It may only be used with explicit
 * permission from the respective owner.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { actionFetchStart, actionFetchSuccess, actionFetchFailure, } from './Types';
import { RestServiceUtils, } from '@iva/rest-service-utils';
var _serviceGET = {
    json: function (url, resultFunc, errorFunc, additionalOptions) { return RestServiceUtils.jsonGET(url, resultFunc, errorFunc, additionalOptions); },
    text: function (url, resultFunc, errorFunc, additionalOptions) { return RestServiceUtils.textGET(url, resultFunc, errorFunc, additionalOptions); },
    arrayBuffer: function (url, resultFunc, errorFunc, additionalOptions) { return RestServiceUtils.arrayBufferGET(url, resultFunc, errorFunc, additionalOptions); },
    blob: function (url, resultFunc, errorFunc, additionalOptions) { return RestServiceUtils.blobGET(url, resultFunc, errorFunc, additionalOptions); },
};
var _servicePOST = {
    json: function (url, bodyContentObj, resultFunc, errorFunc, additionalOptions) { return RestServiceUtils.jsonPOST(url, bodyContentObj, resultFunc, errorFunc, additionalOptions); },
    text: function (url, bodyContentObj, resultFunc, errorFunc, additionalOptions) { return RestServiceUtils.textPOST(url, bodyContentObj, resultFunc, errorFunc, additionalOptions); },
    arrayBuffer: function (url, bodyContentObj, resultFunc, errorFunc, additionalOptions) { return RestServiceUtils.arrayBufferPOST(url, bodyContentObj, resultFunc, errorFunc, additionalOptions); },
    blob: function (url, bodyContentObj, resultFunc, errorFunc, additionalOptions) { return RestServiceUtils.blobPOST(url, bodyContentObj, resultFunc, errorFunc, additionalOptions); },
};
var _servicePUT = {
    json: function (url, bodyContentObj, resultFunc, errorFunc, additionalOptions) { return RestServiceUtils.jsonPUT(url, bodyContentObj, resultFunc, errorFunc, additionalOptions); },
    text: function (url, bodyContentObj, resultFunc, errorFunc, additionalOptions) { return RestServiceUtils.textPUT(url, bodyContentObj, resultFunc, errorFunc, additionalOptions); },
    arrayBuffer: function (url, bodyContentObj, resultFunc, errorFunc, additionalOptions) { return RestServiceUtils.arrayBufferPUT(url, bodyContentObj, resultFunc, errorFunc, additionalOptions); },
    blob: function (url, bodyContentObj, resultFunc, errorFunc, additionalOptions) { return RestServiceUtils.blobPUT(url, bodyContentObj, resultFunc, errorFunc, additionalOptions); },
};
var _serviceDELETE = {
    json: function (url, bodyContentObj, resultFunc, errorFunc, additionalOptions) { return RestServiceUtils.jsonDELETE(url, bodyContentObj, resultFunc, errorFunc, additionalOptions); },
    text: function (url, bodyContentObj, resultFunc, errorFunc, additionalOptions) { return RestServiceUtils.textDELETE(url, bodyContentObj, resultFunc, errorFunc, additionalOptions); },
    arrayBuffer: function (url, bodyContentObj, resultFunc, errorFunc, additionalOptions) { return RestServiceUtils.arrayBufferDELETE(url, bodyContentObj, resultFunc, errorFunc, additionalOptions); },
    blob: function (url, bodyContentObj, resultFunc, errorFunc, additionalOptions) { return RestServiceUtils.blobDELETE(url, bodyContentObj, resultFunc, errorFunc, additionalOptions); },
};
var _servicePATCH = {
    json: function (url, bodyContentObj, resultFunc, errorFunc, additionalOptions) { return RestServiceUtils.jsonPATCH(url, bodyContentObj, resultFunc, errorFunc, additionalOptions); },
    text: function (url, bodyContentObj, resultFunc, errorFunc, additionalOptions) { return RestServiceUtils.textPATCH(url, bodyContentObj, resultFunc, errorFunc, additionalOptions); },
    arrayBuffer: function (url, bodyContentObj, resultFunc, errorFunc, additionalOptions) { return RestServiceUtils.arrayBufferPATCH(url, bodyContentObj, resultFunc, errorFunc, additionalOptions); },
    blob: function (url, bodyContentObj, resultFunc, errorFunc, additionalOptions) { return RestServiceUtils.blobPATCH(url, bodyContentObj, resultFunc, errorFunc, additionalOptions); },
};
var StoreUtils = /** @class */ (function () {
    function StoreUtils() {
    }
    ////////////////////////   Reduce   ////////////////////////
    StoreUtils.getFetchState = function (action) {
        switch (action.payload.meta) {
            case "fetching":
                return this.getFetchState_fetching();
            case "success":
                return this.getFetchState_success(action.payload.data);
            case "failure":
                return this.getFetchState_failure(action.payload.data);
            default:
                throw new Error("Unknown action meta type, expected one of [\"fetching\", \"success\", \"failure\"]");
        }
    };
    /**
     * Returns a standard store object reflecting the state of the action.
     * The data component is preserved when the resource is fetching, but reset to null if failed.
     */
    StoreUtils.getUpdatingFetchState = function (action, currentState) {
        switch (action.payload.meta) {
            case "fetching":
                return __assign(__assign({}, this.getFetchState_fetching()), { data: currentState.data });
            case "success":
                return this.getFetchState_success(action.payload.data);
            case "failure":
                return this.getFetchState_failure(action.payload.data);
            default:
                throw new Error("Unknown action meta type, expected one of [\"fetching\", \"success\", \"failure\"]");
        }
    };
    /**
     * Returns a standard store object reflecting the state of the action.
     * The data component is preserved when the resource is fetching or failed.
     */
    StoreUtils.getSafeFetchState = function (action, currentState) {
        switch (action.payload.meta) {
            case "fetching":
                return __assign(__assign({}, this.getFetchState_fetching()), { data: currentState.data });
            case "success":
                return this.getFetchState_success(action.payload.data);
            case "failure":
                return __assign(__assign({}, this.getFetchState_failure(action.payload.data)), { data: currentState.data });
            default:
                throw new Error("Unknown action meta type, expected one of [\"fetching\", \"success\", \"failure\"]");
        }
    };
    /**
     * Returns a standard store object to represent the 'init' state of a REST service request.
     */
    StoreUtils.getFetchState_init = function () {
        return {
            fetching: false,
            fetchError: null,
            data: null,
        };
    };
    /**
    * Returns a standard store object to represent the initialized 'init' state of a REST service request.
    */
    StoreUtils.getSafeFetchState_init = function (initValue) {
        return {
            fetching: false,
            fetchError: null,
            data: initValue,
        };
    };
    /**
     * Returns a standard store object to represent the 'fetching' state of a REST service request.
     */
    StoreUtils.getFetchState_fetching = function () {
        return {
            fetching: true,
            fetchError: null,
            data: null,
        };
    };
    /**
     * Returns a standard store object to represent the 'success' state of a REST service request.
     */
    StoreUtils.getFetchState_success = function (result) {
        return {
            fetching: false,
            fetchError: null,
            data: result,
        };
    };
    /**
     * Returns a standard store object to represent the 'failure' state of a REST service request.
     */
    StoreUtils.getFetchState_failure = function (error) {
        return {
            fetching: false,
            fetchError: error,
            data: null,
        };
    };
    ////////////////////////   Dispatchers   ////////////////////////
    StoreUtils.dispatchWithoutBody = function (fetchFunc, urlEndpoint, actionType, additionalOptions) {
        return function (dispatch) {
            dispatch(actionFetchStart(actionType));
            fetchFunc(urlEndpoint, function (result) { return dispatch(actionFetchSuccess(actionType, result)); }, function (error) { return dispatch(actionFetchFailure(actionType, error)); }, additionalOptions);
        };
    };
    StoreUtils.dispatchWithBody = function (fetchFunc, urlEndpoint, bodyContentObj, actionType, additionalOptions) {
        return function (dispatch) {
            dispatch(actionFetchStart(actionType));
            fetchFunc(urlEndpoint, bodyContentObj, function (result) { return dispatch(actionFetchSuccess(actionType, result)); }, function (error) { return dispatch(actionFetchFailure(actionType, error)); }, additionalOptions);
        };
    };
    ////////////////////////   API functions   ////////////////////////
    /**
     * Sends a GET request to a REST endpoint URL, and dispatches the corresponding actions
     * (fetching, success, failure) to represent the state of this async fetch operation in the store.
    */
    StoreUtils.jsonGET = function (urlEndpoint, actionType, additionalOptions) {
        return this.dispatchWithoutBody(_serviceGET.json, urlEndpoint, actionType, additionalOptions);
    };
    /**
     * Sends a POST request to a REST endpoint URL, and dispatches the corresponding actions
     * (fetching, success, failure) to represent the state of this async fetch operation in the store.
    */
    StoreUtils.jsonPOST = function (urlEndpoint, bodyContentObj, actionType, additionalOptions) {
        return this.dispatchWithBody(_servicePOST.json, urlEndpoint, bodyContentObj, actionType, additionalOptions);
    };
    /**
      * Sends a PUT request to a REST endpoint URL, and dispatches the corresponding actions
      * (fetching, success, failure) to represent the state of this async fetch operation in the store.
     */
    StoreUtils.jsonPUT = function (urlEndpoint, bodyContentObj, actionType, additionalOptions) {
        return this.dispatchWithBody(_servicePUT.json, urlEndpoint, bodyContentObj, actionType, additionalOptions);
    };
    /**
      * Sends a DELETE request to a REST endpoint URL, and dispatches the corresponding actions
      * (fetching, success, failure) to represent the state of this async fetch operation in the store.
     */
    StoreUtils.jsonDELETE = function (urlEndpoint, bodyContentObj, actionType, additionalOptions) {
        return this.dispatchWithBody(_serviceDELETE.json, urlEndpoint, bodyContentObj, actionType, additionalOptions);
    };
    /**
      * Sends a PATCH request to a REST endpoint URL, and dispatches the corresponding actions
      * (fetching, success, failure) to represent the state of this async fetch operation in the store.
     */
    StoreUtils.jsonPATCH = function (urlEndpoint, bodyContentObj, actionType, additionalOptions) {
        return this.dispatchWithBody(_servicePATCH.json, urlEndpoint, bodyContentObj, actionType, additionalOptions);
    };
    /**
     * Sends a GET request to a REST endpoint URL, and dispatches the corresponding actions
     * (fetching, success, failure) to represent the state of this async fetch operation in the store.
    */
    StoreUtils.textGET = function (urlEndpoint, actionType, additionalOptions) {
        return this.dispatchWithoutBody(_serviceGET.text, urlEndpoint, actionType, additionalOptions);
    };
    /**
     * Sends a POST request to a REST endpoint URL, and dispatches the corresponding actions
     * (fetching, success, failure) to represent the state of this async fetch operation in the store.
    */
    StoreUtils.textPOST = function (urlEndpoint, bodyContentObj, actionType, additionalOptions) {
        return this.dispatchWithBody(_servicePOST.text, urlEndpoint, bodyContentObj, actionType, additionalOptions);
    };
    /**
      * Sends a PUT request to a REST endpoint URL, and dispatches the corresponding actions
      * (fetching, success, failure) to represent the state of this async fetch operation in the store.
     */
    StoreUtils.textPUT = function (urlEndpoint, bodyContentObj, actionType, additionalOptions) {
        return this.dispatchWithBody(_servicePUT.text, urlEndpoint, bodyContentObj, actionType, additionalOptions);
    };
    /**
      * Sends a DELETE request to a REST endpoint URL, and dispatches the corresponding actions
      * (fetching, success, failure) to represent the state of this async fetch operation in the store.
     */
    StoreUtils.textDELETE = function (urlEndpoint, bodyContentObj, actionType, additionalOptions) {
        return this.dispatchWithBody(_serviceDELETE.text, urlEndpoint, bodyContentObj, actionType, additionalOptions);
    };
    /**
      * Sends a PATCH request to a REST endpoint URL, and dispatches the corresponding actions
      * (fetching, success, failure) to represent the state of this async fetch operation in the store.
     */
    StoreUtils.textPATCH = function (urlEndpoint, bodyContentObj, actionType, additionalOptions) {
        return this.dispatchWithBody(_servicePATCH.text, urlEndpoint, bodyContentObj, actionType, additionalOptions);
    };
    /**
     * Sends a GET request to a REST endpoint URL, and dispatches the corresponding actions
     * (fetching, success, failure) to represent the state of this async fetch operation in the store.
    */
    StoreUtils.arrayBufferGET = function (urlEndpoint, actionType, additionalOptions) {
        return this.dispatchWithoutBody(_serviceGET.arrayBuffer, urlEndpoint, actionType, additionalOptions);
    };
    /**
     * Sends a POST request to a REST endpoint URL, and dispatches the corresponding actions
     * (fetching, success, failure) to represent the state of this async fetch operation in the store.
    */
    StoreUtils.arrayBufferPOST = function (urlEndpoint, bodyContentObj, actionType, additionalOptions) {
        return this.dispatchWithBody(_servicePOST.arrayBuffer, urlEndpoint, bodyContentObj, actionType, additionalOptions);
    };
    /**
      * Sends a PUT request to a REST endpoint URL, and dispatches the corresponding actions
      * (fetching, success, failure) to represent the state of this async fetch operation in the store.
     */
    StoreUtils.arrayBufferPUT = function (urlEndpoint, bodyContentObj, actionType, additionalOptions) {
        return this.dispatchWithBody(_servicePUT.arrayBuffer, urlEndpoint, bodyContentObj, actionType, additionalOptions);
    };
    /**
      * Sends a DELETE request to a REST endpoint URL, and dispatches the corresponding actions
      * (fetching, success, failure) to represent the state of this async fetch operation in the store.
     */
    StoreUtils.arrayBufferDELETE = function (urlEndpoint, bodyContentObj, actionType, additionalOptions) {
        return this.dispatchWithBody(_serviceDELETE.arrayBuffer, urlEndpoint, bodyContentObj, actionType, additionalOptions);
    };
    /**
      * Sends a PATCH request to a REST endpoint URL, and dispatches the corresponding actions
      * (fetching, success, failure) to represent the state of this async fetch operation in the store.
     */
    StoreUtils.arrayBufferPATCH = function (urlEndpoint, bodyContentObj, actionType, additionalOptions) {
        return this.dispatchWithBody(_servicePATCH.arrayBuffer, urlEndpoint, bodyContentObj, actionType, additionalOptions);
    };
    /**
     * Sends a GET request to a REST endpoint URL, and dispatches the corresponding actions
     * (fetching, success, failure) to represent the state of this async fetch operation in the store.
    */
    StoreUtils.blobGET = function (urlEndpoint, actionType, additionalOptions) {
        return this.dispatchWithoutBody(_serviceGET.blob, urlEndpoint, actionType, additionalOptions);
    };
    /**
     * Sends a POST request to a REST endpoint URL, and dispatches the corresponding actions
     * (fetching, success, failure) to represent the state of this async fetch operation in the store.
    */
    StoreUtils.blobPOST = function (urlEndpoint, bodyContentObj, actionType, additionalOptions) {
        return this.dispatchWithBody(_servicePOST.blob, urlEndpoint, bodyContentObj, actionType, additionalOptions);
    };
    /**
      * Sends a PUT request to a REST endpoint URL, and dispatches the corresponding actions
      * (fetching, success, failure) to represent the state of this async fetch operation in the store.
     */
    StoreUtils.blobPUT = function (urlEndpoint, bodyContentObj, actionType, additionalOptions) {
        return this.dispatchWithBody(_servicePUT.blob, urlEndpoint, bodyContentObj, actionType, additionalOptions);
    };
    /**
      * Sends a DELETE request to a REST endpoint URL, and dispatches the corresponding actions
      * (fetching, success, failure) to represent the state of this async fetch operation in the store.
     */
    StoreUtils.blobDELETE = function (urlEndpoint, bodyContentObj, actionType, additionalOptions) {
        return this.dispatchWithBody(_serviceDELETE.blob, urlEndpoint, bodyContentObj, actionType, additionalOptions);
    };
    /**
      * Sends a PATCH request to a REST endpoint URL, and dispatches the corresponding actions
      * (fetching, success, failure) to represent the state of this async fetch operation in the store.
     */
    StoreUtils.blobPATCH = function (urlEndpoint, bodyContentObj, actionType, additionalOptions) {
        return this.dispatchWithBody(_servicePATCH.blob, urlEndpoint, bodyContentObj, actionType, additionalOptions);
    };
    return StoreUtils;
}());
export default StoreUtils;
//# sourceMappingURL=StoreUtils.js.map